/**
 * 🔐 Web Login Screen for Coach Cedar
 * Dark gradient design matching native LoginScreen.native.tsx
 * Used by AuthGuard/EnhancedAuthGuard on web.
 * Uses useAuth0 from @auth0/auth0-react (no Expo Router). When isAuthenticated becomes true,
 * the guard re-renders and shows children.
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  ActivityIndicator,
  Pressable,
  Animated,
  Linking,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth0 } from '@auth0/auth0-react';
import { auth0Connections } from '../auth/auth0-config';
import { CoachCedarColors } from '../utils/styleConverter';
import { storage } from '../services/storage';
// Static asset imports for web compatibility
import logoReverse from '../../assets/CC_Logo_reverse_top.png';

/** Google "G" logo — official brand colors */
const GoogleLogoSvg = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.1 24.1 0 0 0 0 21.56l7.98-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

/** Apple logo SVG */
const AppleLogoSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#FFFFFF">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

export function LoginScreenWeb() {
  const insets = useSafeAreaInsets();
  const { loginWithRedirect, isLoading: authLoading, error: authError, isAuthenticated } = useAuth0();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [activeAuthFlow, setActiveAuthFlow] = useState<'google' | 'apple' | 'email' | 'signup' | null>(null);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [isReturningUser, setIsReturningUser] = useState(false);

  // Check if returning user on mount
  useEffect(() => {
    async function checkReturningUser() {
      const hasLoggedInBefore = await storage.getItem('has_logged_in_before');
      setIsReturningUser(hasLoggedInBefore === 'true');
    }
    checkReturningUser();
  }, []);

  // Fade in animation on mount
  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();
  }, []);

  // Handle auth errors
  useEffect(() => {
    if (authError) {
      console.error('🔴 Auth Error:', authError);
      setIsAuthenticating(false);
      setActiveAuthFlow(null);
    }
  }, [authError]);

  const handleAppleSignIn = async () => {
    if (isAuthenticating) return;
    setIsAuthenticating(true);
    setActiveAuthFlow('apple');
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: auth0Connections.apple,
        },
      });
    } catch (error) {
      console.error('🔴 LoginScreenWeb: Apple sign in error:', error);
    } finally {
      setIsAuthenticating(false);
      setActiveAuthFlow(null);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isAuthenticating) return;
    setIsAuthenticating(true);
    setActiveAuthFlow('google');
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: auth0Connections.google,
          prompt: 'select_account',
          max_age: 0,
        },
      });
    } catch (error) {
      console.error('🔴 LoginScreenWeb: Google sign in error:', error);
    } finally {
      setIsAuthenticating(false);
      setActiveAuthFlow(null);
    }
  };

  const handleEmailSignIn = async () => {
    if (isAuthenticating) return;
    setIsAuthenticating(true);
    setActiveAuthFlow('email');
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: auth0Connections.email,
          screen_hint: 'signin',
        },
      });
    } catch (error) {
      console.error('🔴 LoginScreenWeb: Email sign in error:', error);
    } finally {
      setIsAuthenticating(false);
      setActiveAuthFlow(null);
    }
  };

  const handleEmailSignUp = async () => {
    if (isAuthenticating) return;
    setIsAuthenticating(true);
    setActiveAuthFlow('signup');
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: auth0Connections.email,
          screen_hint: 'signup',
        },
      });
    } catch (error) {
      console.error('🔴 LoginScreenWeb: Create account error:', error);
    } finally {
      setIsAuthenticating(false);
      setActiveAuthFlow(null);
    }
  };

  const isProcessing = authLoading || isAuthenticating;

  return (
    <View style={styles.container}>
      {/* Dark gradient background via inline style (web-safe linear gradient) */}
      <View style={[StyleSheet.absoluteFillObject, styles.gradientBg]} />
      <SafeAreaView style={styles.safeArea} edges={['top']}>
        <Animated.View style={[styles.contentWrapper, { opacity: fadeAnim }]}>

          {/* Logo — centered in upper section */}
          <View style={styles.logoSection}>
            <Image
              source={logoReverse}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>

          {/* Content — title, buttons, legal; stacked at bottom */}
          <View style={[styles.contentSection, { paddingBottom: 32 + insets.bottom }]}>

            {/* Welcome title */}
            <Text style={styles.welcomeTitle}>
              {isReturningUser ? 'Welcome Back' : 'Welcome'}
            </Text>
            <Text style={styles.subtitle}>
              {isReturningUser ? 'Login to your account' : 'Create your new account'}
            </Text>

            {/* Error display */}
            {authError && (
              <Text style={styles.errorText}>Sign-in failed. Please try again.</Text>
            )}

            {/* Continue with Apple */}
            <Pressable
              style={({ pressed }) => [
                styles.appleButton,
                isProcessing && styles.buttonDisabled,
                pressed && !isProcessing && styles.appleButtonPressed,
              ]}
              onPress={handleAppleSignIn}
              disabled={isProcessing}
            >
              {activeAuthFlow === 'apple' ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <View style={styles.buttonContent}>
                  <AppleLogoSvg />
                  <Text style={styles.appleButtonText}>Continue with Apple</Text>
                </View>
              )}
            </Pressable>

            {/* Continue with Google */}
            <Pressable
              style={({ pressed }) => [
                styles.googleButton,
                isProcessing && styles.buttonDisabled,
                pressed && !isProcessing && styles.googleButtonPressed,
              ]}
              onPress={handleGoogleSignIn}
              disabled={isProcessing}
            >
              {activeAuthFlow === 'google' ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <View style={styles.buttonContent}>
                  <GoogleLogoSvg />
                  <Text style={styles.googleButtonText}>Continue with Google</Text>
                </View>
              )}
            </Pressable>

            {/* Divider */}
            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>or</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Sign in with Email */}
            <Pressable
              style={({ pressed }) => [
                styles.emailButton,
                isProcessing && styles.buttonDisabled,
                pressed && !isProcessing && styles.emailButtonPressed,
              ]}
              onPress={handleEmailSignIn}
              disabled={isProcessing}
            >
              {activeAuthFlow === 'email' ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.emailButtonText}>Sign in with Email</Text>
              )}
            </Pressable>

            {/* Create account link */}
            <Pressable
              onPress={handleEmailSignUp}
              disabled={isProcessing}
              style={({ pressed }) => [
                styles.createAccountLinkWrap,
                pressed && styles.createAccountLinkPressed,
              ]}
            >
              {activeAuthFlow === 'signup' ? (
                <ActivityIndicator color={CoachCedarColors.cedarSubtle} size="small" />
              ) : (
                <Text style={[
                  styles.createAccountLinkText,
                  isProcessing && styles.createAccountLinkDisabled,
                ]}>
                  Create account
                </Text>
              )}
            </Pressable>

            {/* Legal Disclaimer */}
            <Text style={styles.legalDisclaimer}>
              By signing in, you agree to our{' '}
              <Text
                style={styles.legalLink}
                onPress={() => Linking.openURL('https://coachcedar.com/terms')}
              >
                Terms
              </Text>
              {' '}and{' '}
              <Text
                style={styles.legalLink}
                onPress={() => Linking.openURL('https://coachcedar.com/privacy')}
              >
                Privacy Policy
              </Text>
            </Text>
          </View>

        </Animated.View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  // Web-safe gradient background using backgroundImage
  gradientBg: {
    // @ts-ignore — web-only CSS property
    backgroundImage: 'linear-gradient(135deg, #0B1614 0%, #113C2B 25%, #1a5c40 50%, #113C2B 75%, #0B1614 100%)',
    backgroundColor: CoachCedarColors.darkCedar, // fallback
  },
  safeArea: {
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },

  // ─── Logo Section ───────────────────────────────────────────────
  logoSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 16,
  },
  logo: {
    width: 174,
    height: 150,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 3,
  },

  // ─── Content Section ────────────────────────────────────────────
  contentSection: {
    paddingHorizontal: 28,
    paddingTop: 4,
    maxWidth: 440,
    alignSelf: 'center',
    width: '100%',
  },

  // H1 — Welcome title
  welcomeTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: CoachCedarColors.surface,        // White
    textAlign: 'center',
    letterSpacing: -0.5,
    marginBottom: 8,
    lineHeight: 38,
  },

  // Body — Subtitle
  subtitle: {
    fontSize: 15,
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.72)',
    textAlign: 'center',
    marginBottom: 28,
    lineHeight: 22,
  },

  // Error text
  errorText: {
    fontSize: 14,
    color: '#FCA5A5',
    textAlign: 'center',
    marginBottom: 12,
  },

  // ─── Apple Sign-In Button ──────────────────────────────────────
  appleButton: {
    backgroundColor: '#000000',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 8,
  },
  appleButtonPressed: {
    backgroundColor: '#333333',
    transform: [{ scale: 0.98 }],
  },
  appleButtonText: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '600',
    letterSpacing: 0.3,
  },

  // ─── Google Sign-In Button ─────────────────────────────────────
  googleButton: {
    backgroundColor: CoachCedarColors.cedarPrime,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 8,
  },
  googleButtonPressed: {
    // DEEPEN, don't brighten. This button carries a white label on cedarPrime
    // (6.33:1). Pressing to cedarLight dropped it to 3.00:1 for as long as the
    // finger was down; cedarDark keeps it at 12.32:1 and still reads as a press.
    backgroundColor: CoachCedarColors.cedarDark,
    transform: [{ scale: 0.98 }],
  },
  googleButtonText: {
    color: CoachCedarColors.surface,
    fontSize: 17,
    fontWeight: '600',
    letterSpacing: 0.3,
  },

  // Shared button styles
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  buttonDisabled: {
    opacity: 0.6,
  },

  // ─── Divider ────────────────────────────────────────────────────
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginVertical: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
  },
  dividerText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.45)',
    fontWeight: '500',
  },

  // ─── Email Sign-In Button ──────────────────────────────────────
  emailButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.09)',
    borderWidth: 1.5,
    borderColor: 'rgba(255, 255, 255, 0.22)',
  },
  emailButtonPressed: {
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
    borderColor: 'rgba(255, 255, 255, 0.38)',
    transform: [{ scale: 0.98 }],
  },
  emailButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: CoachCedarColors.surface,
    letterSpacing: 0.3,
  },

  // ─── Create account link ───────────────────────────────────────
  createAccountLinkWrap: {
    marginTop: 12,
    alignSelf: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  createAccountLinkText: {
    fontSize: 15,
    color: CoachCedarColors.cedarSubtle,
    textAlign: 'center',
    fontWeight: '500',
  },
  createAccountLinkPressed: {
    opacity: 0.7,
  },
  createAccountLinkDisabled: {
    opacity: 0.5,
  },

  // ─── Legal Disclaimer ─────────────────────────────────────────
  legalDisclaimer: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.38)',
    textAlign: 'center',
    lineHeight: 16,
    marginTop: 20,
  },
  legalLink: {
    color: 'rgba(255, 255, 255, 0.65)',
    fontWeight: '600',
  },
});
